import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  defaultOptionLabel,
  defaultOptionValue,
  filterOptionsByQuery,
  normalizeAndSortOptions,
} from '../../utils/selectOptions';
import '../../styles/searchable-select.css';

/**
 * Shared dropdown: alphabetically sorted options + type-to-search.
 * Drop-in compatible with native <select> onChange({ target: { name, value } }).
 *
 * Set creatable to allow free-text values (e.g. add a new company name).
 */
export default function SearchableSelect({
  id,
  name,
  value,
  onChange,
  options = [],
  placeholder = 'Select…',
  required = false,
  disabled = false,
  className = '',
  error = false,
  creatable = false,
  createLabel = (q) => `Add “${q}”`,
  getOptionLabel = defaultOptionLabel,
  getOptionValue = defaultOptionValue,
  getOptionListLabel,
  getOptionGroup,
  'aria-label': ariaLabel,
}) {
  const autoId = useId();
  const fieldId = id || `searchable-select-${autoId}`;
  const rootRef = useRef(null);
  const menuRef = useRef(null);
  const searchRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState(null);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);

  const sorted = useMemo(
    () =>
      normalizeAndSortOptions(options, {
        getOptionLabel,
        getOptionValue,
        getOptionListLabel,
        getOptionGroup,
      }),
    [options, getOptionLabel, getOptionValue, getOptionListLabel, getOptionGroup]
  );

  const filtered = useMemo(() => filterOptionsByQuery(sorted, query), [sorted, query]);

  const trimmedQuery = String(query || '').trim();
  const exactLabelMatch = useMemo(() => {
    if (!trimmedQuery) return false;
    const q = trimmedQuery.toLowerCase();
    return sorted.some((o) => o.label.toLowerCase() === q);
  }, [sorted, trimmedQuery]);

  const showCreate = Boolean(creatable && trimmedQuery && !exactLabelMatch);

  const listItems = useMemo(() => {
    const items = [];
    if (showCreate) {
      items.push({
        type: 'create',
        value: trimmedQuery,
        label: createLabel(trimmedQuery),
      });
    }
    filtered.forEach((opt) => {
      items.push({
        type: 'option',
        value: opt.value,
        label: opt.label,
        listLabel: opt.listLabel || opt.label,
        group: opt.group || '',
        index: opt.index,
      });
    });
    return items;
  }, [showCreate, trimmedQuery, createLabel, filtered]);

  const selected = useMemo(() => {
    const str = value === undefined || value === null ? '' : String(value);
    if (!str) return null;
    const found = sorted.find((o) => o.value === str);
    if (found) return found;
    // Free-text custom value only (not a pending numeric master id while options load)
    if (creatable && !/^\d+$/.test(str)) return { value: str, label: str };
    return null;
  }, [sorted, value, creatable]);

  const emitChange = (nextValue) => {
    if (typeof onChange !== 'function') return;
    onChange({
      target: {
        name: name || '',
        value: nextValue,
      },
      currentTarget: {
        name: name || '',
        value: nextValue,
      },
    });
  };

  useLayoutEffect(() => {
    if (!open) {
      setMenuStyle(null);
      return undefined;
    }
    const place = () => {
      const trigger = rootRef.current?.querySelector('.searchable-select-trigger');
      if (!trigger) return;
      const rect = trigger.getBoundingClientRect();
      const gap = 4;
      const spaceBelow = window.innerHeight - rect.bottom - gap - 8;
      const spaceAbove = rect.top - gap - 8;
      const openUp = spaceBelow < 220 && spaceAbove > spaceBelow;
      const maxHeight = Math.max(160, Math.min(360, openUp ? spaceAbove : spaceBelow));
      const next = {
        left: rect.left,
        width: rect.width,
        maxHeight,
      };
      if (openUp) next.bottom = window.innerHeight - rect.top + gap;
      else next.top = rect.bottom + gap;
      setMenuStyle(next);
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      const target = e.target;
      const insideField = rootRef.current && rootRef.current.contains(target);
      const insideMenu = menuRef.current && menuRef.current.contains(target);
      if (!insideField && !insideMenu) {
        // Creatable: commit typed text on outside click (parity with old datalist)
        if (creatable && trimmedQuery) {
          if (exactLabelMatch) {
            const match = sorted.find(
              (o) => o.label.toLowerCase() === trimmedQuery.toLowerCase()
            );
            if (match) emitChange(match.value);
          } else {
            emitChange(trimmedQuery);
          }
        }
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open, creatable, trimmedQuery, exactLabelMatch, sorted, name, onChange]);

  useEffect(() => {
    if (open) {
      setActiveIndex(0);
      setTimeout(() => searchRef.current?.focus(), 0);
    }
  }, [open, query]);

  const selectOption = (opt) => {
    emitChange(opt ? opt.value : '');
    setOpen(false);
    setQuery('');
  };

  const selectListItem = (item) => {
    if (!item) return;
    selectOption({ value: item.value, label: item.label });
  };

  const confirmActive = () => {
    const item = listItems[activeIndex];
    if (item) {
      selectListItem(item);
      return;
    }
    if (showCreate) {
      selectListItem({ type: 'create', value: trimmedQuery, label: trimmedQuery });
    }
  };

  const onKeyDown = (e) => {
    const inSearch = e.target === searchRef.current;
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
      setQuery('');
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, Math.max(listItems.length - 1, 0)));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      confirmActive();
      return;
    }
    if (e.key === ' ' && !inSearch) {
      e.preventDefault();
    }
  };

  return (
    <div
      ref={rootRef}
      className={`searchable-select ${className}`}
      onKeyDown={onKeyDown}
    >
      {/*
        type=hidden is barred from constraint validation — avoids Chrome
        "invalid form control is not focusable" when required + zero-size text input.
        App forms use React state; name still supports FormData if needed.
      */}
      {name ? (
        <input type="hidden" name={name} value={value ?? ''} disabled={disabled} />
      ) : null}

      <button
        id={fieldId}
        type="button"
        className={`searchable-select-trigger ${disabled ? 'is-disabled' : ''} ${
          error ? 'is-error' : ''
        }`}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-required={required || undefined}
        aria-invalid={error || undefined}
        aria-label={ariaLabel || placeholder}
        onClick={() => {
          if (disabled) return;
          setOpen((v) => !v);
          if (open) setQuery('');
        }}
      >
        <span
          className={`searchable-select-trigger-label ${
            selected ? '' : 'is-placeholder'
          }`}
        >
          {selected ? selected.label : placeholder}
        </span>
        <span className="searchable-select-chevron" aria-hidden>
          {open ? '▲' : '▼'}
        </span>
      </button>

      {open && menuStyle
        ? createPortal(
        <div
          ref={menuRef}
          className="searchable-select-menu is-portaled"
          style={menuStyle}
          role="listbox"
        >
          <input
            ref={searchRef}
            type="search"
            className="searchable-select-search"
            placeholder={
              creatable ? 'Type to search or add…' : 'Type to search…'
            }
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                e.stopPropagation();
                confirmActive();
              }
            }}
            autoComplete="off"
          />
          <div className="searchable-select-list">
            {!required ? (
              <button
                type="button"
                role="option"
                aria-selected={!selected}
                className={`searchable-select-option ${!selected ? 'is-selected' : ''}`}
                onClick={() => selectOption(null)}
              >
                {placeholder}
              </button>
            ) : null}
            {listItems.length === 0 ? (
              <div className="searchable-select-empty">No matches</div>
            ) : (
              listItems.map((item, index) => {
                const prev = listItems[index - 1];
                const showGroup =
                  item.type === 'option' &&
                  item.group &&
                  item.group !== prev?.group;
                return (
                  <div
                    key={
                      item.type === 'create'
                        ? `create-${item.value}`
                        : `${item.value}-${item.index}`
                    }
                  >
                    {showGroup ? (
                      <div className="searchable-select-group" role="presentation">
                        {item.group}
                      </div>
                    ) : null}
                    <button
                      type="button"
                      role="option"
                      aria-selected={
                        item.type !== 'create' && selected?.value === item.value
                      }
                      className={`searchable-select-option ${
                        item.type === 'create' ? 'is-create' : ''
                      } ${
                        item.type !== 'create' && selected?.value === item.value
                          ? 'is-selected'
                          : ''
                      } ${index === activeIndex ? 'is-active' : ''}`}
                      onMouseEnter={() => setActiveIndex(index)}
                      onClick={() => selectListItem(item)}
                    >
                      {item.listLabel || item.label}
                    </button>
                  </div>
                );
              })
            )}
          </div>
        </div>,
        document.body
        )
        : null}
    </div>
  );
}
